import { useEffect, useRef } from 'react';
import usePortal from 'react-useportal';
import useSSR from 'use-ssr';

const DRAWER_CONTAINER = 'product-listing-fe__drawer-container';

const headCss = `
#product-listing-root[aria-hidden=true] a[href],
#product-listing-root[aria-hidden=true] area[href], 
#product-listing-root[aria-hidden=true] input:not([disabled]), 
#product-listing-root[aria-hidden=true] select:not([disabled]), 
#product-listing-root[aria-hidden=true] textarea:not([disabled]), 
#product-listing-root[aria-hidden=true] button:not([disabled]), 
#product-listing-root[aria-hidden=true] [tabindex]:not([disabled]), 
#product-listing-root[aria-hidden=true] [contenteditable=true]:not([disabled]),

.drawer__first-level[aria-hidden=true] a[href],
.drawer__first-level[aria-hidden=true] area[href], 
.drawer__first-level[aria-hidden=true] input:not([disabled]), 
.drawer__first-level[aria-hidden=true] select:not([disabled]), 
.drawer__first-level[aria-hidden=true] textarea:not([disabled]), 
.drawer__first-level[aria-hidden=true] button:not([disabled]), 
.drawer__first-level[aria-hidden=true] [tabindex]:not([disabled]), 
.drawer__first-level[aria-hidden=true] [contenteditable=true]:not([disabled]),

header[aria-hidden=true] a[href],
header[aria-hidden=true] area[href], 
header[aria-hidden=true] input:not([disabled]), 
header[aria-hidden=true] select:not([disabled]), 
header[aria-hidden=true] textarea:not([disabled]), 
header[aria-hidden=true] button:not([disabled]), 
header[aria-hidden=true] [tabindex]:not([disabled]), 
header[aria-hidden=true] [contenteditable=true]:not([disabled]),

main[aria-hidden=true] a[href],
main[aria-hidden=true] area[href], 
main[aria-hidden=true] input:not([disabled]), 
main[aria-hidden=true] select:not([disabled]), 
main[aria-hidden=true] textarea:not([disabled]), 
main[aria-hidden=true] button:not([disabled]), 
main[aria-hidden=true] [tabindex]:not([disabled]), 
main[aria-hidden=true] [contenteditable=true]:not([disabled]),

footer[aria-hidden=true] a[href],
footer[aria-hidden=true] area[href], 
footer[aria-hidden=true] input:not([disabled]), 
footer[aria-hidden=true] select:not([disabled]), 
footer[aria-hidden=true] textarea:not([disabled]), 
footer[aria-hidden=true] button:not([disabled]), 
footer[aria-hidden=true] [tabindex]:not([disabled]), 
footer[aria-hidden=true] [contenteditable=true]:not([disabled]){
    visibility: hidden;
}`;

export function useMainDrawer(isPanelOpen: boolean) {
  const { isBrowser } = useSSR();

  const headRef = useRef<HTMLHeadElement | null>(null);
  const bodyRef = useRef<HTMLBodyElement | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const styleRef = useRef<HTMLStyleElement | null>(null);

  if (isBrowser && (!bodyRef.current || !containerRef.current)) {
    bodyRef.current = document.querySelector('body');
    containerRef.current = document.createElement('div');
    containerRef.current.classList.add(DRAWER_CONTAINER);
    bodyRef.current?.appendChild(containerRef.current);
  }
  if (isBrowser && (!headRef.current || !styleRef.current)) {
    headRef.current = document.querySelector('head');
    styleRef.current = document.createElement('style');
    styleRef.current.innerHTML = headCss;
    headRef.current?.appendChild(styleRef.current);
  }

  const options = containerRef.current !== null ? { bindTo: containerRef.current } : undefined;

  const { Portal } = usePortal(options);

  const headerRef = useRef<HTMLElement | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);
  const footerRef = useRef<HTMLElement | null>(null);

  if (isBrowser && (!headerRef.current || !mainRef.current || !footerRef.current)) {
    headerRef.current = document.querySelector('header');
    mainRef.current = document.querySelector('main');
    footerRef.current = document.querySelector('footer');
  }

  // uncoment the code to make the feature working localy
  /*
    const localRef = useRef<Element | null>(null);
    if (isBrowser && !localRef.current) {
      // eslint-disable-next-line prefer-destructuring
      localRef.current = document.querySelector('#product-listing-root');
    }
    useEffect(() => {
      if (isPanelOpen) {
        localRef.current?.setAttribute('aria-hidden', 'true');
      } else {
        localRef.current?.removeAttribute('aria-hidden');
      }

      return () => {
        localRef.current?.removeAttribute('aria-hidden');
      };
    }, [isPanelOpen]);
  */

  useEffect(() => {
    if (isPanelOpen) {
      headerRef.current?.setAttribute('aria-hidden', 'true');
      mainRef.current?.setAttribute('aria-hidden', 'true');
      footerRef.current?.setAttribute('aria-hidden', 'true');
    } else {
      headerRef.current?.removeAttribute('aria-hidden');
      mainRef.current?.removeAttribute('aria-hidden');
      footerRef.current?.removeAttribute('aria-hidden');
    }

    return () => {
      headerRef.current?.removeAttribute('aria-hidden');
      mainRef.current?.removeAttribute('aria-hidden');
      footerRef.current?.removeAttribute('aria-hidden');
    };
  }, [isPanelOpen]);

  useEffect(
    () => () => {
      if (styleRef.current) {
        headRef.current?.removeChild(styleRef.current);
      }
      if (containerRef.current) {
        bodyRef.current?.removeChild(containerRef.current);
      }
      headRef.current = null;
      containerRef.current = null;
      bodyRef.current = null;
      styleRef.current = null;
    },
    [],
  );

  return Portal;
}
